/**
 * API Logger Diagnostic Tool
 * Captures and analyzes the exact payload being sent to identify validation issues
 */

import { ApiLogDto } from '../types/logManagement';

export class ApiLoggerDiagnostic {
  private static capturedPayloads: any[] = [];
  private static validationErrors: any[] = [];

  /**
   * Capture a payload before sending to backend
   */
  static capturePayload(payload: ApiLogDto): void {
    const captured = {
      timestamp: new Date().toISOString(),
      payload: JSON.parse(JSON.stringify(payload)), // Deep clone
      payloadSize: JSON.stringify(payload).length,
      fieldCount: Object.keys(payload).length
    };

    this.capturedPayloads.push(captured);
    
    // Keep only last 10 payloads
    if (this.capturedPayloads.length > 10) {
      this.capturedPayloads.shift();
    }

    console.log('ApiLoggerDiagnostic: Captured payload', captured);
  }

  /**
   * Capture validation error response
   */
  static captureValidationError(error: any, originalPayload?: ApiLogDto): void {
    const captured = {
      timestamp: new Date().toISOString(),
      error: error,
      errorMessage: error?.message || 'Unknown error',
      errorResponse: error?.response?.data || null,
      originalPayload: originalPayload ? JSON.parse(JSON.stringify(originalPayload)) : null
    };

    this.validationErrors.push(captured);
    
    // Keep only last 10 errors
    if (this.validationErrors.length > 10) {
      this.validationErrors.shift();
    }

    console.error('ApiLoggerDiagnostic: Captured validation error', captured);
  }

  /**
   * Analyze captured data to identify common issues
   */
  static analyzeIssues(): {
    commonMissingFields: string[];
    commonInvalidFields: string[];
    payloadSizeIssues: boolean;
    recommendations: string[];
  } {
    const analysis = {
      commonMissingFields: [] as string[],
      commonInvalidFields: [] as string[],
      payloadSizeIssues: false,
      recommendations: [] as string[]
    };

    // Analyze payload sizes
    const avgSize = this.capturedPayloads.reduce((sum, p) => sum + p.payloadSize, 0) / this.capturedPayloads.length;
    if (avgSize > 50000) { // 50KB
      analysis.payloadSizeIssues = true;
      analysis.recommendations.push('Payload size is large (>50KB). Consider reducing metadata size.');
    }

    // Analyze validation errors
    this.validationErrors.forEach(error => {
      const errorMsg = error.errorMessage.toLowerCase();
      
      if (errorMsg.includes('required') || errorMsg.includes('missing')) {
        analysis.recommendations.push('Check for missing required fields in ApiLogDto');
      }
      
      if (errorMsg.includes('invalid') || errorMsg.includes('format')) {
        analysis.recommendations.push('Check field formats (dates, numbers, strings)');
      }
      
      if (errorMsg.includes('validation')) {
        analysis.recommendations.push('Backend validation rules may have changed');
      }
    });

    return analysis;
  }

  /**
   * Generate sample valid payload for testing
   */
  static generateSamplePayload(): ApiLogDto {
    return {
      id: `test_${Date.now()}`,
      timestamp: new Date().toISOString(),
      level: 'info',
      message: 'Test API log message',
      source: 'ApiLoggerDiagnostic',
      category: 'test',
      method: 'GET',
      endpoint: '/api/test',
      statusCode: 200,
      responseTime: 100,
      tags: ['test', 'diagnostic'],
      metadata: {
        test: true,
        diagnostic: 'sample payload'
      }
    };
  }

  /**
   * Validate payload against expected structure
   */
  static validatePayload(payload: ApiLogDto): {
    isValid: boolean;
    missingRequired: string[];
    invalidFields: string[];
    warnings: string[];
  } {
    const result = {
      isValid: true,
      missingRequired: [] as string[],
      invalidFields: [] as string[],
      warnings: [] as string[]
    };

    // Check required fields
    const requiredFields = ['id', 'timestamp', 'level', 'message', 'source', 'method', 'endpoint', 'statusCode', 'responseTime'];
    
    requiredFields.forEach(field => {
      if (!payload[field as keyof ApiLogDto]) {
        result.missingRequired.push(field);
        result.isValid = false;
      }
    });

    // Check field types and formats
    if (payload.timestamp && !this.isValidISODate(payload.timestamp)) {
      result.invalidFields.push('timestamp (invalid ISO format)');
      result.isValid = false;
    }

    if (payload.level && !['error', 'warning', 'info', 'debug'].includes(payload.level)) {
      result.invalidFields.push('level (invalid value)');
      result.isValid = false;
    }

    if (payload.statusCode && (typeof payload.statusCode !== 'number' || payload.statusCode < 100 || payload.statusCode > 599)) {
      result.invalidFields.push('statusCode (invalid HTTP status)');
      result.isValid = false;
    }

    if (payload.responseTime && (typeof payload.responseTime !== 'number' || payload.responseTime < 0)) {
      result.invalidFields.push('responseTime (invalid number)');
      result.isValid = false;
    }

    // Check for potential issues
    if (payload.metadata && JSON.stringify(payload.metadata).length > 10000) {
      result.warnings.push('metadata is very large (>10KB)');
    }

    if (payload.tags && payload.tags.length > 20) {
      result.warnings.push('too many tags (>20)');
    }

    return result;
  }

  /**
   * Get diagnostic report
   */
  static getDiagnosticReport(): {
    capturedPayloads: number;
    validationErrors: number;
    lastError: any;
    analysis: any;
    samplePayload: ApiLogDto;
  } {
    return {
      capturedPayloads: this.capturedPayloads.length,
      validationErrors: this.validationErrors.length,
      lastError: this.validationErrors[this.validationErrors.length - 1] || null,
      analysis: this.analyzeIssues(),
      samplePayload: this.generateSamplePayload()
    };
  }

  /**
   * Clear all captured data
   */
  static clearData(): void {
    this.capturedPayloads = [];
    this.validationErrors = [];
    console.log('ApiLoggerDiagnostic: Cleared all captured data');
  }

  /**
   * Export captured data for backend team analysis
   */
  static exportForBackendTeam(): string {
    const report = {
      generatedAt: new Date().toISOString(),
      summary: {
        totalPayloadsCaptured: this.capturedPayloads.length,
        totalValidationErrors: this.validationErrors.length,
        analysis: this.analyzeIssues()
      },
      sampleValidPayload: this.generateSamplePayload(),
      capturedPayloads: this.capturedPayloads,
      validationErrors: this.validationErrors,
      recommendations: [
        'Check backend validation rules for /api/LogManagement/api-logs endpoint',
        'Verify all required fields are properly handled',
        'Ensure field types match expected schema',
        'Test with the provided sample payload',
        'Check for any recent changes to ApiLogDto validation'
      ]
    };

    return JSON.stringify(report, null, 2);
  }

  private static isValidISODate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString.includes('T');
  }
}

export default ApiLoggerDiagnostic;